// components/KdsSettingsModal/KdsSettingsModal.jsx
import React, { useState, useEffect } from "react";
import { XMarkIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import "./KdsSettingsModal.css";

const KdsSettingsModal = ({ open, onClose }) => {
  const [config, setConfig] = useState({
    screenDisplays: [],
    orderBumpingMode: "AUTO",
    overrideAutoPause: false,
    approveOrders: false,
    rejectOrders: false,
    reQueueOrders: false,
    removeOrders: false,
    reverseCompletedOrders: false,
    allowComplimentary: false,
    enableInvoicePrinting: false,
    allowMarkingItems: false,
  });

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = () => {
    const appConfigData = localStorage.getItem("appConfig");
    const appConfig = appConfigData ? JSON.parse(appConfigData) : {};

    setConfig({
      screenDisplays: appConfig?.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens || [],
      orderBumpingMode: appConfig?.settings?.ORDER_BUMPING_MODE?.orderBumpingMode || "AUTO",
      overrideAutoPause: appConfig?.enabledFeatures?.OVERRIDE_AUTO_PAUSE || false,
      approveOrders: appConfig?.enabledFeatures?.APPROVE_ORDERS || false,
      rejectOrders: appConfig?.enabledFeatures?.REJECT_ORDERS || false,
      reQueueOrders: appConfig?.enabledFeatures?.REQUEUE_ORDERS || false,
      removeOrders: appConfig?.enabledFeatures?.REMOVE_ORDERS || false,
      reverseCompletedOrders: appConfig?.enabledFeatures?.REVERSE_COMPLETED_ORDERS || false,
      allowComplimentary: appConfig?.enabledFeatures?.ALLOW_COMPLIMENTARY_ORDERS || false,
      enableInvoicePrinting: appConfig?.enabledFeatures?.ENABLE_INVOICE_PRINTING_FROM_KDS || false,
      allowMarkingItems: appConfig?.enabledFeatures?.ALLOW_MARKING_ITEMS_FROM_KDS || false,
    });
  };

  if (!open) return null;

  return (
    <div className="kds-settings-modal-overlay">
      <div className="kds-settings-modal">
        <div className="kds-settings-header">
          <h2>Kitchen Display Screen (KDS) Settings</h2>
          <button className="kds-settings-close" onClick={onClose}>
            <XMarkIcon />
          </button>
        </div>

        <div className="kds-settings-content">
          {/* RMS Update Notice */}
          <div className="kds-settings-notice">
            <ExclamationCircleIcon className="kds-settings-notice-icon" />
            <div className="kds-settings-notice-content">
              <strong>View Only Mode</strong>
              <p>To update these settings, please go to the Restaurant Management System (RMS)</p>
            </div>
          </div>

          {/* Screen Display Settings */}
          <section className="kds-settings-section">
            <h3>Screen Display Settings</h3>
            <div className="kds-settings-display-grid">
              <div className="kds-settings-display-item">
                <span className="kds-settings-display-label">KDS:</span>
                <span
                  className={`kds-settings-status ${
                    config?.screenDisplays.includes("KDS") ? "enabled" : "disabled"
                  }`}
                >
                  {config?.screenDisplays?.includes("KDS") ? "ENABLED" : "DISABLED"}
                </span>
              </div>
              <div className="kds-settings-display-item">
                <span className="kds-settings-display-label">ODS:</span>
                <span
                  className={`kds-settings-status ${
                    config?.screenDisplays.includes("ODS") ? "enabled" : "disabled"
                  }`}
                >
                  {config?.screenDisplays?.includes("ODS") ? "ENABLED" : "DISABLED"}
                </span>
              </div>
              <div className="kds-settings-display-item">
                <span className="kds-settings-display-label">KOT:</span>
                <span
                  className={`kds-settings-status ${
                    config?.screenDisplays.includes("KOT") ? "enabled" : "disabled"
                  }`}
                >
                  {config?.screenDisplays?.includes("KOT") ? "ENABLED" : "DISABLED"}
                </span>
              </div>
            </div>
          </section>

          {/* Order Bumping & Flow */}
          <section className="kds-settings-section">
            <h3>Order Bumping & Flow</h3>
            <div className="kds-settings-info-row">
              <span className="kds-settings-info-label">Order Bumping Mode:</span>
              <span className="kds-settings-info-value">
                {config?.orderBumpingMode === "AUTO" ? "Auto-Bump orders" : "Manual-Bump orders"}
              </span>
            </div>
            <div className="kds-settings-info-row">
              <span className="kds-settings-info-label">Override auto-pause setting:</span>
              <span
                className={`kds-settings-status ${
                  config?.overrideAutoPause ? "enabled" : "disabled"
                }`}
              >
                {config?.overrideAutoPause ? "YES" : "NO"}
              </span>
            </div>
          </section>

          {/* KDS Order Actions */}
          <section className="kds-settings-section">
            <h3>KDS Order Actions</h3>
            <div className="kds-settings-split">
              <div className="kds-settings-column">
                <p className="kds-settings-subtitle">Enabled Actions:</p>
                <div className="kds-settings-actions-list">
                  <div className="kds-settings-action-item">
                    <span className="kds-settings-action-label">Approve Orders:</span>
                    <span
                      className={`kds-settings-status ${
                        config?.approveOrders ? "enabled" : "disabled"
                      }`}
                    >
                      {config?.approveOrders ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                  <div className="kds-settings-action-item">
                    <span className="kds-settings-action-label">Reject Orders:</span>
                    <span
                      className={`kds-settings-status ${
                        config?.rejectOrders ? "enabled" : "disabled"
                      }`}
                    >
                      {config?.rejectOrders ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                  <div className="kds-settings-action-item">
                    <span className="kds-settings-action-label">Re-Queue Orders:</span>
                    <span
                      className={`kds-settings-status ${
                        config?.reQueueOrders ? "enabled" : "disabled"
                      }`}
                    >
                      {config?.reQueueOrders ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                  <div className="kds-settings-action-item">
                    <span className="kds-settings-action-label">Remove Orders:</span>
                    <span
                      className={`kds-settings-status ${
                        config?.removeOrders ? "enabled" : "disabled"
                      }`}
                    >
                      {config?.removeOrders ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                  <div className="kds-settings-action-item">
                    <span className="kds-settings-action-label">Reverse Completed Orders:</span>
                    <span
                      className={`kds-settings-status ${
                        config?.reverseCompletedOrders ? "enabled" : "disabled"
                      }`}
                    >
                      {config?.reverseCompletedOrders ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="kds-settings-column">
                <p className="kds-settings-subtitle">Additional Settings:</p>
                <div className="kds-settings-info-row">
                  <span className="kds-settings-info-label">Allow Complimentary Orders:</span>
                  <span
                    className={`kds-settings-status ${
                      config?.allowComplimentary ? "enabled" : "disabled"
                    }`}
                  >
                    {config?.allowComplimentary ? "YES" : "NO"}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Printing & Inventory */}
          <section className="kds-settings-section">
            <h3>Printing & Inventory</h3>
            <div className="kds-settings-split">
              <div className="kds-settings-column">
                <div className="kds-settings-info-row">
                  <span className="kds-settings-info-label">Invoice Printing from KDS:</span>
                  <span
                    className={`kds-settings-status ${
                      config?.enableInvoicePrinting ? "enabled" : "disabled"
                    }`}
                  >
                    {config?.enableInvoicePrinting ? "ENABLED" : "DISABLED"}
                  </span>
                </div>
                {config?.enableInvoicePrinting && (
                  <p className="kds-settings-note">
                    Print invoices if the printer is mapped to KOT + Invoice.
                  </p>
                )}
              </div>

              <div className="kds-settings-column">
                <div className="kds-settings-info-row">
                  <span className="kds-settings-info-label">
                    Mark Items as Out of Stock / In Stock:
                  </span>
                  <span
                    className={`kds-settings-status ${
                      config?.allowMarkingItems ? "enabled" : "disabled"
                    }`}
                  >
                    {config?.allowMarkingItems ? "ENABLED" : "DISABLED"}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="kds-settings-footer">
          <button className="kds-settings-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default KdsSettingsModal;
